<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UCard :ui="{ rounded: 'rounded-lg' }">
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">
                        Settle Invoice
                    </h3>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                </div>
            </template>
            <div>
                <p>This action is non-reversible. Are you sure you want to void this invoice?</p>
            </div>
            <template #footer>
                <div class="flex items-center justify-end space-x-2">
                    <UButton @click="dismiss()" size="lg" color="white">
                        Cancel
                    </UButton>
                    <UButton @click="confirm()" size="lg" :loading="confirming" color="red">
                        Void
                    </UButton>
                </div>
            </template>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import type { Invoice } from '~/types/invoice'
import { UserRole } from '~/types/user';

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [void]
}>()

const props = defineProps<{
    open: boolean
    invoice?: Invoice
}>()

const toast = useToast()
const auth = useAuthStore()
const client = useSupabaseClient()

const confirming = ref<boolean>(false)

async function confirm() {
    if (!auth.hasRole(UserRole.admin)) {
        toast.add({
            title: 'You do not have permission to void invoices',
            color: 'red',
            icon: 'i-ph-warning-circle-duotone'
        })

        return dismiss()
    }

    if (props.invoice?.id) {
        if (props.invoice.settled_at || props.invoice.voided_at) {
            toast.add({
                title: 'Invoice is already settled or voided.',
                color: 'red',
                icon: 'i-ph-warning-circle-duotone'
            })

            return dismiss()
        }

        confirming.value = true

        const { error } = await client.from('invoices')
            .update({ voided_at: new Date().toISOString() } as never)
            .eq('id', props.invoice.id)

        if (error) {
            toast.add({
                title: 'Could not void invoice!',
                description: 'Please try again later.',
                color: 'red',
                icon: 'i-ph-x-circle-duotone'
            })
        } 
        else {
            await client.from('subscriptions')
                .update({ invoice_id: null } as never)
                .eq('invoice_id', props.invoice.id)

            toast.add({
                title: 'Invoice voided successfully!',
                color: 'green',
                icon: 'i-ph-check-circle-duotone'
            })

            emit('reload')
        }

        confirming.value = false
        dismiss()
    }
}

function dismiss() {
    emit('update:open', false)
}
</script>